import React, { useEffect, useState } from "react";

import { useRouter } from "next/router";
import * as Sentry from "@sentry/nextjs";
import { AutocorpLogo } from "@/components/AutocorpLogo";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Button } from "torque-ui/components/Button";

import { useStore } from "@/store";
import { STEPS } from "@/api/constants";
import type { IPageProps } from "@/templates/MobileOnly";
import { MobileOnly, getServerSideProps } from "@/templates/MobileOnly";

import DriversLicenseIcon from "@assets/svg/drivers-license.svg";
import StepOneIcon from "@assets/svg/step-one-icon.svg";
import CheckCircle from "@assets/svg/check-circle.svg";
import { InlineInquiry } from "@/components/PersonaInlineInquiry";

export default function StepOne({ isMobile }: IPageProps) {
  const router = useRouter();
  const {
    state: {
      leadId,
      nextStep,
      clientToken,
      loading,
      hydrated,
      error, // token error
    },
    actions: {
      setTemplateType,
      setNextStep,
      reset,
      loadTemplate,
    }
  } = useStore();

  const [stateLoaded, setStateLoaded] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [invalidLeadId, setInvalidLeadId] = useState(false);
  const [idVerified, setIdVerified] = useState(false);
  const [idDisabled, setIdDisabled] = useState(false);
  const [showPersona, setShowPersona] = useState(false);
  const [personaInquiryId, setPersonaInquiryId] = useState<string | undefined>();
  const [personaLanguage, setPersonaLanguage] = useState("en");

  /*
    If user closes the window/app and comes back,
    redirect user to where they left off
  */
  useEffect(() => {
    if (nextStep) {
      if (nextStep.includes("done") || nextStep.includes("complete")) {
        setShowLoading(false);
        return;
      }
      /* Selfie step is being removed from API-Only integration templates */
      // if (nextStep.includes("selfie")) {
      //   router.push(`${leadId}/selfie`);
      //   return;
      // }
      if (nextStep !== STEPS.UPLOAD_DOCUMENT_FRONT) {
        router.push(`${leadId}/take-photo`);
        
      }
    }
  }, [leadId, nextStep]);

  /* Grab and set Template Type */
  useEffect(() => {
    const getTemplateType = async () => {
      const {
        success,
        templateType,
        provider,
        redirectUrl,
        alreadyVerified,
        isIdEnabled,
        nextStep: resNextStep,
      } = await loadTemplate();
      if (success) {
        if (provider === "persona" && !!redirectUrl) {
          if (router.query.language) {
            setPersonaLanguage(router.query.language as string);
          }
          const inquiryIdQueryParam = redirectUrl.split("?").pop();
          const inquiryId = inquiryIdQueryParam?.split("=").pop();
          setPersonaInquiryId(inquiryId);
          setShowLoading(false);
          setShowPersona(true);
          return;
        }

        setTemplateType(templateType);
        if (!resNextStep) {
          setNextStep(nextStep);
        } else {
          setNextStep(resNextStep);
        }
        Sentry.captureMessage("Template Type Set", {
          level: "info",
          extra: { leadId, templateType },
        });
        if (!isIdEnabled) {
          Sentry.captureMessage(`AVA ID is not enabled for lead: ${leadId}`);
          setIdDisabled(true);
          setShowLoading(false);
          return;
        }
        if (alreadyVerified) {
          Sentry.captureMessage(`Lead is already verified. Lead: ${leadId}`);
          reset();
          setIdVerified(true);
          setShowLoading(false);
          return;
        }

        setShowLoading(false);
      } else {
        setInvalidLeadId(true);
        setShowLoading(false);
      }
    };

    // Set timeout to allow data hydration
    setTimeout(() => {
      setStateLoaded(true);
    }, 500);
    getTemplateType();
  }, [leadId]);

  if (showLoading) {
    return (
      <div className="flex w-full h-screen justify-center">
        <LoadingSpinner className="w-[75px] h-[75px] m-auto absolute top-[45%]" />
      </div>
    );
  }

  if (!stateLoaded) {
    return null;
  }

  if (showPersona) {
    return <InlineInquiry inquiryId={personaInquiryId} language={personaLanguage} />;
  }

  if (invalidLeadId) {
    return (
      <div className="h-full pt-14 pb-12 flex flex-col bg-white" style={{ height: "80vh" }}>
        <main
          className="flex-grow flex flex-col justify-center max-w-xs w-full mx-auto px-4"
        >
          <div className="py-2">
            <div className="text-center">
              <h1 className="text-xl my-4 mx-2 font-bold tracking-wide">Check your Lead ID</h1>
              <p className="text-sm mt-4 mb-4 mx-8">Unable to retrieve lead with id: <b>{leadId}</b></p>
              <p className="text-sm mt-1 mb-10 mx-8">Please check your Lead ID and make sure it's valid.</p>
            </div>
          </div>
        </main>

        <footer className="flex-shrink-0 max-w-7xl w-full mx-auto px-4">
          <nav className="flex flex-col items-center justify-center space-x-4">
            <p className="my-6 w-60 text-center text-sm">By using this service you agree to Autocorp's <a
              href="https://www.autocorp.ai/terms-of-service"
              target="_blank"
              className=""
            >Terms of Services</a> and <a
              href="https://www.autocorp.ai/privacy-policy"
              target="_blank"
              className=""
            >Privacy Policy</a></p>
            <AutocorpLogo />
          </nav>
        </footer>
      </div>
    );
  }

  if (idDisabled) {
    return (
      <div className="h-full pt-14 pb-12 flex flex-col bg-white" style={{ height: "80vh" }}>
        <main
          className="flex-grow flex flex-col justify-center max-w-xs w-full mx-auto px-4"
        >
          <div className="py-2">
            <div className="text-center">
              <h1 className="text-xl my-4 mx-2 font-bold tracking-wide">AVA™ ID is not available</h1>
              <p className="text-sm mt-4 mb-10 mx-8">Please contact an Autocorp Admin to request access to our AVA™ ID product.</p>
            </div>
          </div>
        </main>

        <footer className="flex-shrink-0 max-w-7xl w-full mx-auto px-4">
          <nav className="flex flex-col items-center justify-center space-x-4">
            <p className="my-6 w-60 text-center text-sm">By using this service you agree to Autocorp's <a
              href="https://www.autocorp.ai/terms-of-service"
              target="_blank"
              className=""
            >Terms of Services</a> and <a
              href="https://www.autocorp.ai/privacy-policy"
              target="_blank"
              className=""
            >Privacy Policy</a></p>
            <AutocorpLogo />
          </nav>
        </footer>
      </div>
    );
  }

  if (idVerified) {
    return (
      <div className="h-full pt-14 pb-12 flex flex-col bg-white" style={{ height: "80vh" }}>
        <main
          className="flex-grow flex flex-col justify-center max-w-xs w-full mx-auto px-4"
        >
          <div className="py-2">
            <div className="text-center">
              <CheckCircle className="mx-auto" />
              <h1 className="text-xl my-4 mx-2 font-bold tracking-wide">Your Driver's License has already been verified!</h1>
              <p className="text-sm mt-4 mb-10 mx-8">You can close this window</p>
            </div>
          </div>
        </main>

        <footer className="flex-shrink-0 max-w-7xl w-full mx-auto px-4">
          <nav className="flex flex-col items-center justify-center space-x-4">
            <p className="my-6 w-60 text-center text-sm">By using this service you agree to Autocorp's <a
              href="https://www.autocorp.ai/terms-of-service"
              target="_blank"
              className=""
            >Terms of Services</a> and <a
              href="https://www.autocorp.ai/privacy-policy"
              target="_blank"
              className=""
            >Privacy Policy</a></p>
            <AutocorpLogo />
          </nav>
        </footer>
      </div>
    );
  }

  return (
    <MobileOnly isMobile={isMobile}>
      <div className="h-full pt-14 pb-12 flex flex-col bg-white" style={{ height: "80vh" }}>
        <main className="flex-grow flex flex-col justify-center max-w-xs w-full mx-auto px-4">
          <div className="flex-shrink-0 flex justify-center">
            <StepOneIcon />
          </div>
          <div className="pt-2 pb-16">
            <div className="text-center">
              <p className="text-sm">Scan your Drivers License</p>
              <h1 className="mt-10 text-xl font-semibold text-gray-900 tracking-tight">Drivers License</h1>

              <div className="mt-2 flex-shrink-0 flex justify-center">
                <DriversLicenseIcon />
              </div>

              <p className="mt-2 text-sm">Take a photo of the front of your drivers license.</p>
              <p className="mt-8 text-xs font-bold">Do not close the page during this process.</p>
              {hydrated && !clientToken ? <p className="w-full text-sm text-red-600 my-10 mb-0">
                  {error ?? "Something went wrong. We are unable to verify you at this time"}
                </p> : null}
              
              <Button 
                className="self-center rounded-full my-[2.5rem] py-[10px] px-[25px]"
                onClick={() => router.push(`${leadId}/take-photo`)}
                loading={loading}
              >
                Verify Now
              </Button>
              <p className="mt-2 text-sm">By using this service you agree to Autocorp's <a
                href="https://www.autocorp.ai/terms-of-service"
                target="_blank"
                className=""
              >Terms of Services</a> and <a
                href="https://www.autocorp.ai/privacy-policy"
                target="_blank"
                className=""
              >Privacy Policy</a></p>
            </div>
          </div>
        </main>

        <footer className="flex-shrink-0 max-w-7xl w-full mx-auto pb-6 px-4">
          <nav className="flex justify-center space-x-4">
            <AutocorpLogo />
          </nav>
        </footer>
      </div>
    </MobileOnly>
  )
}

export { getServerSideProps }